'use client'

import { resetAnalyticsIdentity } from '@mentora/feature-components/analytics'
import { Avatar, AvatarFallback, AvatarImage, Button } from '@mentora/ui-components'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { authClient } from '@/lib/auth-client'
import { STOREFRONT } from '@/lib/storefront'

function getInitials(name: string) {
  return name
    .split(' ')
    .map(w => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export function UserMenu() {
  const t = useTranslations('userMenu')
  const { data: session, isPending } = authClient.useSession()
  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const router = useRouter()

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  if (isPending) {
    return <div className="h-8 w-8 animate-pulse rounded-full bg-(--color-muted)" />
  }

  if (!session?.user) {
    return (
      <Button asChild size="sm">
        <Link href="/sign-in">{t('signIn')}</Link>
      </Button>
    )
  }

  const user = session.user

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        className="cursor-pointer rounded-full outline-none ring-2 ring-transparent transition-shadow hover:ring-(--color-border) focus-visible:ring-(--color-primary)"
      >
        <Avatar className="h-8 w-8">
          {user.image && <AvatarImage src={user.image} alt={user.name} />}
          <AvatarFallback className="text-xs">
            {getInitials(user.name)}
          </AvatarFallback>
        </Avatar>
      </button>

      {open && (
        <div className="animate-fade-in absolute right-0 top-full z-50 mt-2 w-52 overflow-hidden rounded-lg border border-(--color-border) bg-(--color-card) shadow-lg shadow-overlay/30">
          <div className="border-b border-(--color-border) px-4 py-3">
            <p className="truncate text-sm font-medium text-(--color-foreground)">
              {user.name}
            </p>
            <p className="truncate text-xs text-(--color-muted-foreground)">
              {user.email}
            </p>
          </div>

          <div className="py-1">
            <Link
              href="/profile"
              onClick={() => setOpen(false)}
              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-(--color-foreground) no-underline transition-colors hover:bg-(--color-muted)"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              {t('profile')}
            </Link>

            <Link
              href="/orders"
              onClick={() => setOpen(false)}
              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-(--color-foreground) no-underline transition-colors hover:bg-(--color-muted)"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
                <path d="M3 6h18" />
                <path d="M16 10a4 4 0 0 1-8 0" />
              </svg>
              {t('orders')}
            </Link>

            <button
              type="button"
              onClick={async () => {
                setOpen(false)
                await authClient.signOut()
                resetAnalyticsIdentity(STOREFRONT)
                router.push('/')
                router.refresh()
              }}
              className="flex w-full cursor-pointer items-center gap-2 border-none bg-transparent px-4 py-2 text-sm text-(--color-foreground) transition-colors hover:bg-(--color-muted)"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              {t('logOut')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
